import React, { useState, useEffect } from 'react';
import { formatInTenantTz } from '../../../lib/datetime';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { 
  Lightbulb, 
  CheckCircle, 
  X,
  Clock,
  Bot,
  Trash2,
  RefreshCw,
  Eye
} from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import SolutionReviewModal from './SolutionReviewModal';
import { normalizeBusinessRole } from '../../../lib/role';

// D17 — client role gate (defense-in-depth; the backend is authoritative). Consumes the canonical
// Web role normalizer (lib/role.js), which collapses administrator + group_administrator (+ space/
// hyphen variants) to 'admin'. So the semantic approver set here is simply {admin, manager}.

const SolutionBankReview = () => {
  const { t } = useLanguage();
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [reviewTarget, setReviewTarget] = useState(null);
  const { toast } = useToast();
  // makeAuthenticatedRequest comes from the CRM auth (Firebase token), but the BUSINESS ROLE gate MUST
  // read the canonical business-role source used by every working manager surface (BusinessSidebar,
  // EscalationManager, SolutionBankManager) — useAuth().user — NOT useCRMAuth().user, whose role does not
  // reliably project the staff business role at runtime (a real manager was mis-gated to grey text).
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  // canApprove gates BOTH opening the review modal AND (inside the modal) enabling Approve. Opening review
  // is intentionally allowed for INVALID candidates too — the modal enables Sanitize/Edit/Reject/Return and
  // only DISABLES the Approve button; an approver must never be blocked from opening review to fix a candidate.
  const canApprove = ['admin', 'manager'].includes(normalizeBusinessRole(user?.role));

  useEffect(() => {
    fetchSolutions();
  }, [activeTab]);

  const fetchSolutions = async () => {
    setLoading(true);
    const businessId = currentBusiness?.brandId;
    if (!businessId) {
      setLoading(false);
      return;
    }
    try {
      const statusMap = { pending: 'pending_approval', approved: 'approved', rejected: 'rejected' };
      const status = statusMap[activeTab] || 'pending_approval';

      const response = await makeAuthenticatedRequest(
        `/api/v1/solutions/${businessId}?status=${status}`
      );

      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const data = await response.json();
      setSolutions(data.solutions || []);
    } catch (error) {
      console.error('Error fetching solutions:', error);
      setSolutions([]);
      toast({
        title: t('sb.toast.loadFailed'),
        description: error.message || t('sb.review.loadFailedDesc'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };


  // D17 — direct one-click Approve/Reject removed. Approval, rejection and return-for-correction now
  // happen only inside the full SolutionReviewModal after a complete review (backend re-validates).

  const handleRevoke = async (solutionId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/v1/solutions/${solutionId}/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Revoked by manager' }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      toast({ title: t('sb.review.toast.revoked'), description: t('sb.review.toast.revokedDesc') });
      fetchSolutions();
    } catch (error) {
      toast({ title: t('sb.review.toast.revokeFailed'), description: error.message, variant: 'destructive' });
    }
  };

  const handleResubmit = async (solutionId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/v1/solutions/${solutionId}/resubmit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      toast({ title: t('sb.review.toast.resubmitted'), description: t('sb.review.toast.resubmittedDesc') });
      fetchSolutions();
    } catch (error) {
      toast({ title: t('sb.review.toast.resubmitFailed'), description: error.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (solutionId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/v1/solutions/${solutionId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      toast({ title: t('sb.review.toast.deleted'), description: t('sb.review.toast.deletedDesc') });
      fetchSolutions();
    } catch (error) {
      toast({ title: t('sb.review.toast.deleteFailed'), description: error.message, variant: 'destructive' });
    }
  };


  const formatDate = (timestamp) => {
    if (!timestamp) return t('sb.review.unknownDate');
    return formatInTenantTz(timestamp, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.9) {
      return <Badge className="bg-green-500">{t('sb.review.confHigh', { pct: (confidence * 100).toFixed(0) })}</Badge>;
    } else if (confidence >= 0.7) {
      return <Badge className="bg-yellow-500">{t('sb.review.confMedium', { pct: (confidence * 100).toFixed(0) })}</Badge>;
    } else {
      return <Badge className="bg-red-500">{t('sb.review.confLow', { pct: (confidence * 100).toFixed(0) })}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>{t('sb.loadingSolutions')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Lightbulb className="w-8 h-8 text-yellow-600" />
            {t('sb.review.title')}
          </h1>
          <p className="text-gray-600 mt-1">{t('sb.review.subtitle')}</p>
        </div>
        <Button onClick={fetchSolutions} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('common.refresh')}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">
            <Clock className="w-4 h-4 mr-2" />
            {t('sb.review.pendingReview')} ({solutions.length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            <CheckCircle className="w-4 h-4 mr-2" />
            {t('sb.review.approvedTab')}
          </TabsTrigger>
          <TabsTrigger value="rejected">
            <X className="w-4 h-4 mr-2" />
            {t('sb.review.rejectedTab')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4 mt-4">
          {solutions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Bot className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>{t('sb.review.noPending')}</p>
                <p className="text-sm mt-1">{t('sb.review.noPendingSub')}</p>
              </CardContent>
            </Card>
          ) : (
            solutions.map((solution) => (
              <Card key={solution.id} className="border-yellow-200">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-yellow-600" />
                        {solution.title}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-2">
                        <Badge variant="outline">{solution.category_label || solution.category}</Badge>
                        {typeof solution.confidence === 'number' && getConfidenceBadge(solution.confidence)}
                        {solution.source === 'ai_generated' ? (
                          <Badge className="bg-blue-500">
                            <Bot className="w-3 h-3 mr-1" />
                            {solution.source_channel === 'ai_voice' ? t('sb.review.aiVoice')
                              : solution.source_channel === 'ai_chat' ? t('sb.review.aiChat')
                              : t('sb.review.aiGenerated')}
                            {solution.remediation_source === 'openai_fallback' ? ` · ${t('sb.review.openaiFallback')}` : ''}
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            {/* Backend projection resolves submitted_by -> submitted_by_name (staff/customer
                                display name or origin label). Never render the raw submitted_by id; fall back
                                to the localized generic "staff" label. */}
                            {t('sb.review.submittedBy', { name: solution.submitted_by_name || t('sb.review.staff') })}
                          </Badge>
                        )}
                        <span className="text-xs text-gray-500">{formatDate(solution.created_at)}</span>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">{t('sb.review.description')}</p>
                    <p className="text-sm text-gray-600">{solution.description}</p>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">{t('sb.review.resolutionSteps')}</p>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                      {solution.resolution}
                    </p>
                  </div>

                  {(solution.keywords && solution.keywords.length > 0) && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">{t('sb.review.keywords')}</p>
                      <div className="flex flex-wrap gap-2">
                        {solution.keywords.map((keyword, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {solution.source_conversation_id && (
                    <div className="flex items-center text-xs text-gray-500">
                      <Eye className="w-3 h-3 mr-1" />
                      {t('sb.review.sourceConversation', { id: solution.source_conversation_id ? String(solution.source_conversation_id).slice(-6).toUpperCase() : '' })}
                    </div>
                  )}

                  <div className="flex gap-2 pt-2 border-t">
                    {canApprove ? (
                      <Button
                        onClick={() => setReviewTarget(solution)}
                        className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Review Solution
                      </Button>
                    ) : (
                      <div className="flex-1 text-sm text-gray-500 flex items-center">
                        Pending manager review
                      </div>
                    )}
                    {canApprove && (
                      <Button
                        variant="outline"
                        aria-label="Delete solution"
                        title="Delete solution"
                        className="border-red-300 text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(solution.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4 mt-4">
          {solutions.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
              <p>{t('sb.review.noApproved')}</p>
            </CardContent></Card>
          ) : solutions.map((solution) => (
            <Card key={solution.id} className="border-green-200">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-base">{solution.title}</CardTitle>
                  <Badge className="bg-green-500">{t('sb.review.activeInAi')}</Badge>
                </div>
                <CardDescription>{solution.category} · {t('sb.review.approvedOn', { date: formatDate(solution.reviewed_at) })}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded mb-3">{solution.resolution || solution.description}</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-red-400 text-red-600 hover:bg-red-50"
                    onClick={() => handleRevoke(solution.id)}
                  >
                    <X className="w-4 h-4 mr-1" />{t('sb.review.revoke')}
                  </Button>
                  <Button variant="outline" aria-label="Delete solution" title="Delete solution" className="border-red-300 text-red-500 hover:bg-red-50" onClick={() => handleDelete(solution.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="rejected" className="space-y-4 mt-4">
          {solutions.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">
              <X className="w-12 h-12 mx-auto mb-4 text-red-400" />
              <p>{t('sb.review.noRejected')}</p>
            </CardContent></Card>
          ) : solutions.map((solution) => (
            <Card key={solution.id} className="border-red-200">
              <CardHeader>
                <CardTitle className="text-base">{solution.title}</CardTitle>
                <CardDescription>{solution.category} · {t('sb.review.rejectedOn', { date: formatDate(solution.reviewed_at) })}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded mb-3">{solution.description}</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleResubmit(solution.id)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />{t('sb.review.resubmit')}
                  </Button>
                  <Button variant="outline" aria-label="Delete solution" title="Delete solution" className="border-red-300 text-red-500 hover:bg-red-50" onClick={() => handleDelete(solution.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {reviewTarget && canApprove && (
        <SolutionReviewModal
          solution={reviewTarget}
          businessId={currentBusiness?.brandId}
          makeAuthenticatedRequest={makeAuthenticatedRequest}
          toast={toast}
          t={t}
          onClose={() => setReviewTarget(null)}
          onDone={() => { setReviewTarget(null); fetchSolutions(); }}
        />
      )}
    </div>
  );
};

export default SolutionBankReview;